import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Eye, Sparkles, Loader2, Heart } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { consultationService, ConsultationHistory } from '../services/consultationApi';
import { contactService, SinastriaHistoryEntry } from '../services/api';
import { deckFromSpread } from '../utils/deck';

type Tab = 'tarot' | 'sinastria';

const REL_LABEL: Record<string, string> = {
  romantico: '❤️ Romântico',
  amizade: '🤝 Amizade',
  familiar: '👨‍👩‍👧 Familiar',
  profissional: '💼 Profissional',
};

const ENTRY_LABEL: Record<string, string> = {
  analise: '🔮 Análise',
  conselho: '💡 Conselho',
  pergunta: '❓ Pergunta',
  composto: '🌗 Mapa Composto',
  davison: '🕊️ Mapa de Davison',
};

// Rota de destino do ícone de olho, conforme o tipo de entrada.
const ENTRY_ROUTE: Record<string, string> = {
  composto: 'composto',
  davison: 'davison',
};

// Converte **negrito** do texto gerado pela Aurora em <strong>, em vez de mostrar os
// asteriscos literalmente (mesmo parser usado em SinastriaPage.tsx).
function renderFormatted(text: string, cls = "text-sm text-white/75 leading-relaxed"): React.ReactNode {
  return text.split('\n').filter(l => l.trim()).map((line, i) => {
    const parts: React.ReactNode[] = [];
    let last = 0;
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match: RegExpExecArray | null;
    while ((match = boldRegex.exec(line)) !== null) {
      if (match.index > last) parts.push(line.slice(last, match.index));
      parts.push(<strong key={match.index} className="text-white font-semibold">{match[1]}</strong>);
      last = match.index + match[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return <p key={i} className={cls}>{parts}</p>;
  });
}

const ConsultationHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('tarot');

  const [consultations, setConsultations] = useState<ConsultationHistory[]>([]);
  const [loadingTarot, setLoadingTarot] = useState(true);
  const [errorTarot, setErrorTarot] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [sinastriaHistory, setSinastriaHistory] = useState<SinastriaHistoryEntry[]>([]);
  const [loadingSinastria, setLoadingSinastria] = useState(false);
  const [errorSinastria, setErrorSinastria] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  useEffect(() => { loadConsultations(); }, []);

  useEffect(() => {
    if (tab === 'sinastria' && sinastriaHistory.length === 0 && !loadingSinastria) {
      loadSinastriaHistory();
    }
  }, [tab]);

  const loadConsultations = async (skip = 0) => {
    try {
      setLoadingTarot(true);
      setErrorTarot(null);
      const response = await consultationService.getConsultationHistory(skip, 20);
      if (skip === 0) setConsultations(response.consultations);
      else setConsultations(prev => [...prev, ...response.consultations]);
      setHasMore(response.consultations.length === 20);
      setPage(Math.floor(skip / 20) + 1);
    } catch (error: any) {
      setErrorTarot(error.message || 'Erro ao carregar histórico de consultas');
    } finally {
      setLoadingTarot(false);
    }
  };

  const loadSinastriaHistory = async () => {
    try {
      setLoadingSinastria(true);
      setErrorSinastria(null);
      const data = await contactService.sinastriaHistory();
      setSinastriaHistory(data);
    } catch (error: any) {
      setErrorSinastria(error.message || 'Erro ao carregar histórico de sinastria');
    } finally {
      setLoadingSinastria(false);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getSpreadDisplayName = (spreadType: string) => {
    const names: Record<string, string> = {
      celtic_cross: 'Cruz Celta',
      three_cards: 'Três Cartas',
      yes_no: 'Sim ou Não',
      single_card: 'Uma Carta',
      astrological_mandala: 'Mandala Astrológica',
    };
    return names[spreadType] || spreadType;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-mystical mb-4">Acesso negado</h2>
          <Link to="/login" className="px-6 py-3 bg-gradient-to-r from-gold-600 to-gold-700 text-white rounded-lg">
            Fazer Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      <div className="flex items-center justify-between p-6 border-b border-white/20">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </Link>
        <h1 className="text-2xl font-mystical font-bold">HISTÓRICO</h1>
        <div className="w-20" />
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl mb-8" style={{ background: 'rgba(255,255,255,0.05)' }}>
          <button
            onClick={() => setTab('tarot')}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
            style={tab === 'tarot'
              ? { background: 'linear-gradient(to right, #7c3aed, #8b5cf6)', color: '#fff' }
              : { color: 'rgba(255,255,255,0.5)' }}
          >
            <Sparkles size={15} /> Consultas
          </button>
          <button
            onClick={() => setTab('sinastria')}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
            style={tab === 'sinastria'
              ? { background: 'linear-gradient(to right, #be185d, #c026d3)', color: '#fff' }
              : { color: 'rgba(255,255,255,0.5)' }}
          >
            <Heart size={15} /> Sinastria
          </button>
        </div>

        {/* ── TAB TAROT ── */}
        {tab === 'tarot' && (
          <>
            {loadingTarot && consultations.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-10 h-10 animate-spin text-gold-400" />
              </div>
            )}

            {errorTarot && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-6 text-center mb-8">
                <p className="text-red-300 mb-4">{errorTarot}</p>
                <button onClick={() => loadConsultations(0)} className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">
                  Tentar Novamente
                </button>
              </div>
            )}

            {consultations.length > 0 && (
              <div className="space-y-5">
                {consultations.map((c, i) => (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-gold-400/50 transition-all group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Meta discreta: data + tiragem + pergunta do usuário */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap text-xs text-gray-400">
                          <Calendar size={13} className="flex-shrink-0" />
                          <span>{formatDate(c.created_at)}</span>
                          {deckFromSpread(c.spread_type).key !== 'aurora' && (
                            <span className="px-2 py-0.5 bg-gold-600/30 text-gold-200 rounded-full">
                              {deckFromSpread(c.spread_type).label}
                            </span>
                          )}
                          <span className="px-2 py-0.5 bg-mystical-600/40 text-mystical-200 rounded-full">
                            {c.spread_name || getSpreadDisplayName(c.spread_type)}
                          </span>
                        </div>
                        {c.question && (
                          <p className="text-gray-400 text-xs italic mb-2 truncate">"{c.question}"</p>
                        )}

                        {/* Interpretação da Aurora — é o valor entregue, fica em destaque */}
                        <p className="text-white text-[15px] leading-relaxed line-clamp-3">
                          {c.general_interpretation || c.summary}
                        </p>

                        {/* Miniaturas das cartas sorteadas */}
                        {c.cards && c.cards.length > 0 && (
                          <div className="flex gap-1.5 mt-3 flex-wrap">
                            {c.cards.slice(0, 8).map((card, idx) => (
                              card.image_url ? (
                                <img
                                  key={idx}
                                  src={card.image_url}
                                  alt={card.card_name}
                                  title={card.card_name}
                                  className="w-8 h-12 object-cover rounded-md border border-white/15 flex-shrink-0"
                                  style={card.is_reversed ? { transform: 'rotate(180deg)' } : undefined}
                                />
                              ) : null
                            ))}
                            {c.cards.length > 8 && (
                              <span className="text-xs text-gray-500 self-center">+{c.cards.length - 8}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <Link
                        to={`/consultation/${c.id}`}
                        className="p-3 bg-gold-600/20 hover:bg-gold-600/30 text-gold-400 rounded-lg transition-all group-hover:scale-105 flex-shrink-0"
                      >
                        <Eye size={20} />
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {hasMore && consultations.length > 0 && (
              <div className="text-center mt-8">
                <button
                  onClick={() => loadConsultations(page * 20)}
                  disabled={loadingTarot}
                  className="px-8 py-3 bg-gradient-to-r from-mystical-600 to-mystical-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center gap-2 mx-auto"
                >
                  {loadingTarot ? <><Loader2 className="animate-spin" size={18} />Carregando...</> : 'Carregar Mais'}
                </button>
              </div>
            )}

            {!loadingTarot && consultations.length === 0 && !errorTarot && (
              <div className="text-center py-12">
                <div className="bg-white/10 rounded-2xl p-8 border border-white/20 max-w-md mx-auto">
                  <Sparkles className="w-14 h-14 text-gold-400 mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-mystical font-bold mb-3">Nenhuma consulta ainda</h3>
                  <p className="text-gray-300 mb-6">Que tal começar sua jornada espiritual?</p>
                  <Link to="/dashboard" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold-600 to-gold-700 text-white rounded-lg">
                    <Sparkles size={18} /> Fazer Primeira Consulta
                  </Link>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── TAB SINASTRIA ── */}
        {tab === 'sinastria' && (
          <>
            {loadingSinastria && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-10 h-10 animate-spin text-pink-400" />
              </div>
            )}

            {errorSinastria && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-6 text-center mb-8">
                <p className="text-red-300 mb-4">{errorSinastria}</p>
                <button onClick={loadSinastriaHistory} className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">
                  Tentar Novamente
                </button>
              </div>
            )}

            {sinastriaHistory.length > 0 && (
              <div className="space-y-4">
                {sinastriaHistory.map((entry, i) => {
                  const expanded = expandedIds.has(entry.id);
                  const preview = entry.content.slice(0, 200);
                  const followUpCount = entry.follow_up_qa?.length || 0;
                  return (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 hover:border-pink-400/40 transition-all group"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-base font-semibold text-white">{entry.contact_name}</span>
                              {entry.relationship_type && (
                                <span className="text-xs text-pink-300 bg-pink-500/15 px-2 py-0.5 rounded-full">
                                  {REL_LABEL[entry.relationship_type] || entry.relationship_type}
                                </span>
                              )}
                              <span className="text-xs text-purple-300 bg-purple-500/15 px-2 py-0.5 rounded-full">
                                {ENTRY_LABEL[entry.entry_type] || entry.entry_type}
                              </span>
                              {entry.advice && (
                                <span className="text-xs text-amber-300 bg-amber-500/15 px-2 py-0.5 rounded-full">💡 Conselho</span>
                              )}
                              {followUpCount > 0 && (
                                <span className="text-xs text-cyan-300 bg-cyan-500/15 px-2 py-0.5 rounded-full">
                                  ❓ {followUpCount} pergunta{followUpCount > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-gray-400 text-xs flex-shrink-0">
                              <Calendar size={13} />
                              {formatDate(entry.created_at)}
                            </div>
                          </div>

                          {entry.entry_type === 'pergunta' && entry.question && (
                            <p className="text-sm text-white/60 italic mb-2">"{entry.question}"</p>
                          )}

                          {expanded
                            ? renderFormatted(entry.content)
                            : (
                              <p className="text-sm text-white/75 leading-relaxed">
                                {preview}{entry.content.length > 200 ? '…' : ''}
                              </p>
                            )}

                          {(entry.content.length > 200 || entry.advice || followUpCount > 0) && (
                            <button
                              onClick={() => toggleExpand(entry.id)}
                              className="mt-2 text-xs text-pink-400 hover:text-pink-300 transition-colors"
                            >
                              {expanded ? 'Ver menos ↑' : 'Ver mais ↓'}
                            </button>
                          )}

                          {/* Conselho e perguntas anexados à mesma análise (mesmo momento) */}
                          {expanded && entry.advice && (
                            <div className="mt-4 pt-4 border-t border-white/10">
                              <p className="text-xs font-semibold text-amber-300 mb-2">💡 Conselho</p>
                              {renderFormatted(entry.advice)}
                            </div>
                          )}
                          {expanded && entry.follow_up_qa?.map((qa, qi) => (
                            <div key={qi} className="mt-4 pt-4 border-t border-white/10">
                              <p className="text-xs font-semibold text-cyan-300 mb-1">❓ {qa.question}</p>
                              {renderFormatted(qa.answer)}
                            </div>
                          ))}
                        </div>

                        {entry.contact_id && (
                          <Link
                            to={`/${ENTRY_ROUTE[entry.entry_type] || 'sinastria'}/${entry.contact_id}`}
                            className="ml-2 p-3 bg-pink-600/20 hover:bg-pink-600/30 text-pink-400 rounded-lg transition-all group-hover:scale-105 flex-shrink-0"
                            title="Ver completo"
                          >
                            <Eye size={20} />
                          </Link>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {!loadingSinastria && sinastriaHistory.length === 0 && !errorSinastria && (
              <div className="text-center py-12">
                <div className="bg-white/10 rounded-2xl p-8 border border-white/20 max-w-md mx-auto">
                  <Heart className="w-14 h-14 text-pink-400 mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-mystical font-bold mb-3">Nenhuma sinastria ainda</h3>
                  <p className="text-gray-300 mb-6">Adicione uma conexão e gere a primeira sinastria.</p>
                  <Link to="/profile?tab=conexoes" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg">
                    <Heart size={18} /> Gerenciar Conexões
                  </Link>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConsultationHistoryPage;
